import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  NzTableComponent,
  NzTableModule,
  NzTableQueryParams,
} from 'ng-zorro-antd/table';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { ProactiveStrategyComponent } from './proactive-strategy/proactive-strategy.component';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  CaseBehaviour,
  ManageStudentDrawerParams,
} from '../../../../../features/insights/insights.interface';
import {
  catchError,
  EMPTY,
  finalize,
  map,
  Observable,
  of,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ManageBehaviorStrategiesService } from './manage-behavior-strategies.service';
import { AuditContextService } from '../../../../../features/audit-logs/audit-context.service';
import { BehaviorStrategyItem, StrategyItem } from './strategy.interface';
import { LoaderService } from '../../../../../core/services/loader.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../core/models/error.models';
import { CreateOwnStrategyComponent } from './create-own-strategy/create-own-strategy.component';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { SettingsService } from '../../../../../features/manage-organization/settings/settings.service';
import { TableQueryService } from '../../../../../core/services/table-query.service';
import { TableQueryParams } from '../../../../../core/models/table.models';
import { ActivatedRoute, Router } from '@angular/router';
import { StudentInfoCardComponent } from '../../../../../components/student-info-card/student-info-card.component';
import { AuthService } from '../../../../../core/services/auth.service';
import { CmsRoleAccessService } from '../../../../../core/services/cms-role-access.service';
import { UserRole } from '../../../../activity-dashboard/activity-dashboard.enums';
import { BehaviorInfoCardComponent } from '../../../../../components/behavior-info-card/behavior-info-card.component';
import { VideoModalComponent } from '../../../../../components/video-modal/video-modal.component';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import { InsightMode } from '../../../insights.enums';
import { InsightsDataComponent } from '../../insights-data/insights-data.component';
import { ViewBipDrawerNavigationService } from '../shared/view-bip-drawer-navigation.service';
import {
  BipTemplateGatingService,
  BipTileSection,
} from '../../../../../shared/services/bip-template-gating.service';
import { TemplateModel } from '../../../../bip-template/bip-template.interface';
import {
  StrategySelectionDrawerCloseResult,
  StrategySelectionDrawerComponent,
  StrategyFlowAction,
} from './strategy-selection-drawer/strategy-selection-drawer.component';
import { StrategySelectionService } from './strategy-selection.service';

type StrategyDrawerStep = 'applied' | 'selection' | 'guide';

@Component({
  selector: 'app-manage-behavior-strategies',
  standalone: true,
  imports: [
    NzFormModule,
    SvgIconComponent,
    NzDividerModule,
    NzSelectModule,
    NzRadioModule,
    NzButtonModule,
    NzTableModule,
    NzInputModule,
    NzModalModule,
    NzCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NzRadioModule,
    NzSpinModule,
    StudentInfoCardComponent,
    BehaviorInfoCardComponent,
    VideoModalComponent,
    DrawerHeaderComponent,
    UserDatePipe,
  ],
  templateUrl: './manage-behavior-strategies.component.html',
  styleUrl: './manage-behavior-strategies.component.scss',
})
export class ManageBehaviorStrategiesComponent implements OnInit, OnDestroy {
  @ViewChild('rowSelectionTable')
  rowSelectionTable!: NzTableComponent<StrategyItem>;

  @ViewChild('publishStrategies')
  publishStrategies!: NzTableComponent<StrategyItem>;

  @ViewChild('drawerBehaviorStrategies')
  drawerBehaviorStrategies!: TemplateRef<void>;

  @ViewChild('drawerBehaviorCreate')
  drawerBehaviorCreate!: TemplateRef<void>;

  @ViewChild('drawerTitleData') drawerTitleData!: TemplateRef<void>;

  @ViewChild('drawerSelectionTitle')
  drawerSelectionTitle!: TemplateRef<void>;

  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly auditContextService = inject(AuditContextService);
  private readonly tableQueryService = inject(TableQueryService);
  private readonly settingsService = inject(SettingsService);
  private readonly selectionService = inject(StrategySelectionService);
  private readonly fb = inject(FormBuilder);
  private readonly viewBipDrawerNavigationService = inject(
    ViewBipDrawerNavigationService
  );
  private readonly bipTemplateGatingService = inject(BipTemplateGatingService);
  defaultOptions = [
    { id: 71, value: 'Replacement Behavior' },
    { id: 72, value: 'Proactive Strategy' },
    { id: 73, value: 'Reactive Strategy' },
  ];
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  mode: 'view' | 'edit' | 'create' | 'guide' = 'view';
  currentStep: StrategyDrawerStep = 'applied';

  caseParams!: ManageStudentDrawerParams;
  selectedOption: 'Guide me' | 'Create my own' = 'Guide me';
  selectedId: number | null = null;
  isVisible = false;
  options: number[] = [];
  selectedIds: number[] = [];
  videoUrl = '';
  isVideoVisible = false;
  selectedIdType = '';
  strategyCount = 0;
  userRoles: string[] = [];
  roleEnum = UserRole;
  allowManageStrategy = true;
  bipTemplate: TemplateModel | null = null;

  // Continue-to-Data is template-driven: shown only when Data is the next included tile after
  // Strategy (Data is fixed last, so this simply checks whether the template includes it).
  get isDataTileIncluded(): boolean {
    return (
      this.bipTemplateGatingService.resolveNextIncludedTile(
        this.bipTemplate,
        BipTileSection.Strategy
      ) === BipTileSection.Data
    );
  }
  defaultParams: TableQueryParams = {
    pageIndex: 1,
    pageSize: 10,
    sortField: undefined,
    sortOrder: undefined,
  };

  readonly strategiesForm = this.fb.group({
    behaviorStrategy: [null, [Validators.required]],
    type: [null, [Validators.required]],
    video: [null, [Validators.required]],
  });

  readonly publishStrategies$ =
    this.strategyService.publishStrategiesSubject.pipe(
      map(response => response?.result?.items ?? [])
    );

  pubCount$ = this.strategyService.publishStrategiesSubject.pipe(
    map(response => response?.result?.totalRows ?? 0)
  );

  strategies$ = this.strategyService.strategiesSubject.pipe(
    map(response => response?.result?.items ?? [])
  );

  stratCount$ = this.strategyService.strategiesSubject.pipe(
    map(response => response?.result?.totalRows ?? 0)
  );

  readonly behaviorStrategies$ = this.strategyService.behaviorStrategies$;

  totalRows$ = this.strategyService.totalRows$;

  flattenedStrategies$ = this.behaviorStrategies$.pipe(
    map((items: BehaviorStrategyItem[]) => items?.[0]?.behaviorStrategies || [])
  );

  constructor(
    private readonly loaderService: LoaderService,
    private readonly commonService: CommonService,
    private readonly drawerService: DrawerService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly cmsRoleAccessService: CmsRoleAccessService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(roles => {
        if (roles) {
          this.userRoles = roles;
          this.allowManageStrategy =
            this.cmsRoleAccessService.computeAllowFromCmsRole(this.userRoles, [
              this.roleEnum.CMSManager,
              this.roleEnum.CMSDataEntry,
              this.roleEnum.CMSBrowser,
            ]);
        }
      });
    this.route.queryParams.subscribe(params => {
      this.mode = params['mode'] || 'view';
      this.syncStepFromMode(this.mode);
      if (params['mode']) {
        this.onCancelled();
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
      }
    });
  }

  ngOnInit(): void {
    this.setupDrawerSubscription();
    this.setupSelectionFlowSubscription();
    this.loading$ = this.loaderService.loading$;
    if (this.caseParams.orgId) {
      this.settingsService
        .getOptions(this.caseParams.orgId)
        .pipe(finalize(() => this.loaderService.hide()))
        .subscribe({
          next: data => {
            this.options = data.result.optionIds;
          },
          error: error => {
            console.error('Error fetching options:', error);
          },
        });
    }
  }

  closeManageBehaviorStrategiesDrawer() {
    const drawer = this.drawerService.getDrawerRef(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
    );
    drawer?.close({
      navigationHandled: true,
      isContinueProcessing: this.caseParams.isContinueProcessing ?? false,
    });
  }

  openDataDrawer(behavior: CaseBehaviour): void {
    this.drawerService
      .openDrawer(
        InsightsDataComponent,
        this.drawerTitleData as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          case: this.caseParams.case,
          behavior: behavior,
          mode: InsightMode.VIEW,
          orgId: this.caseParams.case?.student?.organizationId,
          caseBehaviorId: behavior.id,
          isContinueProcessing: this.caseParams.isContinueProcessing ?? false,
        },
        DRAWER_IDS.MANAGE_DATA
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        const drawerResult = result as
          | { navigationHandled?: boolean }
          | undefined;

        if (drawerResult?.navigationHandled) {
          return;
        }
      });
  }

  closeCreateBehaviorStrategiesDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get behavior() {
    return this.caseParams.behavior;
  }

  get isTypeSelected(): boolean {
    return !!this.strategiesForm.get('type')?.value;
  }

  get isSelectionStep(): boolean {
    return this.currentStep === 'selection';
  }

  get isGuideStep(): boolean {
    return this.currentStep === 'guide';
  }

  get isAppliedStep(): boolean {
    return this.currentStep === 'applied';
  }

  get strategyType(): string {
    const typeId = this.strategiesForm.get('type')?.value;
    if (typeId === null) return '';
    const option = this.defaultOptions.find(opt => opt.id === typeId);
    return option ? option.value : '';
  }

  onCheckboxChange(id: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedIds.includes(id)) {
        this.selectedIds.push(id);
      }
      return;
    }
    this.selectedIds = this.selectedIds.filter(item => item !== id);
  }

  handleOptionChange(value: 'Guide me' | 'Create my own'): void {
    this.selectedOption = value;
  }

  handleNext(): void {
    if (this.isSelectionStep && this.selectedOption === 'Guide me') {
      this.handleGuideSelection();
      return;
    }

    if (this.isGuideStep) {
      this.handleGuideNextAction();
      return;
    }

    if (this.isSelectionStep && this.selectedOption === 'Create my own') {
      this.handleCreateOwnOption();
    }
  }

  private handleGuideSelection(): void {
    this.setCurrentStep('guide');
    this.selectedId = null;
    this.selectedIds = [];
  }

  private handleGuideNextAction(): void {
    const hasSelection = this.selectedId || this.selectedIds?.length;
    const isMultiSelect = !this.options.includes(5);

    if (hasSelection && isMultiSelect) {
      this.showModal();
      return;
    }

    if (this.selectedId) {
      const strategy =
        (this.rowSelectionTable?.data as StrategyItem[])?.find(
          item => item.id === this.selectedId
        ) ?? null;

      this.openStrategyDrawer(strategy, 'guide', 'Guide me');
      this.selectedId = null;
    }
  }

  private handleCreateOwnOption(): void {
    const hasSelection = this.selectedId || this.selectedIds?.length;
    const isMultiSelect = !this.options.includes(5);
    const shouldCreateNew =
      (this.options.includes(5) && !this.selectedId) ||
      (!this.options.includes(5) && !this.selectedIds?.length);

    if (shouldCreateNew) {
      this.openStrategyDrawer(null, 'create');
      return;
    }
    if (hasSelection && isMultiSelect) {
      this.showModal();
      return;
    }

    if (this.selectedId) {
      const strategy =
        (this.publishStrategies?.data as StrategyItem[])?.find(
          item => item.id === this.selectedId
        ) ?? null;

      this.openStrategyDrawer(strategy, 'edit', 'Create my own');
      this.selectedId = null;
    }
  }

  private openStrategyDrawer(
    strategy: StrategyItem | null,
    mode: 'edit' | 'create' | 'guide',
    from?: string,
    strategyTypeId?: number
  ): void {
    this.drawerService
      .openDrawer(
        CreateOwnStrategyComponent,
        this.drawerBehaviorCreate as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          strategy,
          behaviorId: this.caseParams?.behavior?.id,
          mode,
          from,
          strategyTypeId:
            strategyTypeId ?? this.strategiesForm.value.type ?? undefined,
          strategyType: this.strategyType,
          caseParams: this.caseParams,
        },
        DRAWER_IDS.CREATE_OWN_STRATEGY
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        const drawerResult = result as
          | { mode?: 'view' | 'create' | 'guide' }
          | undefined;
        const closeMode = drawerResult?.mode;

        if (closeMode === 'view') {
          this.mode = 'view';
          this.setCurrentStep('applied');
          this.refreshAppliedStrategies();
          this.drawerService.closeDrawer(
            DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGY_SELECTION
          );
          this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
        } else {
          this.mode = closeMode ?? this.mode;
          this.refreshAppliedStrategies();
          this.syncStepFromMode(closeMode ?? this.mode);
        }

        this.resetState();
      });
  }

  handleClose() {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES);
  }

  private resetState(): void {
    this.selectedId = null;
    this.selectedIds = [];
  }

  private refreshAppliedStrategies(): void {
    this.loaderService.show();

    this.strategyService
      .getBehaviorStrategyList({
        caseBehaviorId: this.caseParams?.behavior?.id,
        ...this.defaultParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.defaultParams.pageIndex = 1;
          this.defaultParams.pageSize = 10;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  handleOk(): void {
    this.isVisible = false;
    this.setCurrentStep('applied');
    this.refreshAppliedStrategies();
    this.closeSelectionStepDrawer();
    this.resetState();
    this.strategiesForm.get('type')?.setValue(null);
  }

  handleCancel(): void {
    this.isVisible = false;
    this.resetState();
    this.selectionService.emitResetSelection();
  }

  startAddNew(): void {
    if (!this.allowManageStrategy) {
      return;
    }
    this.openSelectionStepDrawer();
  }

  closeSelectionStepDrawer(): void {
    this.drawerService.closeDrawer(
      DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGY_SELECTION
    );
  }

  private openSelectionStepDrawer(): void {
    this.drawerService
      .openDrawer(
        StrategySelectionDrawerComponent,
        this.drawerSelectionTitle as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          caseParams: this.caseParams,
          options: this.options,
          defaultOptions: this.defaultOptions,
        },
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGY_SELECTION
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshAppliedStrategies();
      });
  }

  private setupSelectionFlowSubscription(): void {
    this.selectionService.flowAction$
      .pipe(takeUntil(this.destroy$))
      .subscribe(action => {
        this.handleSelectionDrawerAction(action);
      });
  }

  private handleSelectionDrawerAction(action: StrategyFlowAction): void {
    this.selectedOption = action.source;

    if (action.type === 'open-create-own') {
      this.selectedId = action.strategy?.id ?? null;
      this.openStrategyDrawer(
        action.strategy ?? null,
        action.mode ?? 'create',
        action.from,
        action.strategyTypeId
      );
      return;
    }

    if (action.type === 'add-selected' && action.ids?.length) {
      this.showModal(action.ids);
    }
  }

  private handleSelectionDrawerClose(
    result: StrategySelectionDrawerCloseResult
  ): void {
    if (!result) {
      return;
    }

    this.selectedOption = result.source;

    if (result.type === 'open-create-own') {
      this.selectedId = result.strategy?.id ?? null;
      this.openStrategyDrawer(
        result.strategy ?? null,
        result.mode ?? 'create',
        result.from
      );
      return;
    }

    if (result.type === 'add-selected' && result.ids?.length) {
      this.showModal(result.ids);
    }
  }

  handlePreviousStep(): void {
    if (this.isGuideStep) {
      this.setCurrentStep('selection');
      this.selectedId = null;
      this.selectedIds = [];
      return;
    }

    if (this.isSelectionStep) {
      this.setCurrentStep('applied');
      this.resetState();
    }
  }

  onCancelled(): void {
    if (this.isAppliedStep) {
      const drawer = this.drawerService.getDrawerRef(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      );
      drawer?.close({ navigationHandled: true });
    } else {
      this.setCurrentStep('applied');
      this.resetState();
    }
  }

  closeDataDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.MANAGE_DATA);
  }

  setSelectedId(id: number, type: string): void {
    // Both branches do the same thing
    this.selectedId = id;
    this.selectedIdType = type;
  }

  showModal(selectedStrategyIds?: number[]): void {
    let strategyIds: (number | null)[] = selectedStrategyIds ?? [];

    if (!strategyIds.length) {
      if (this.options.includes(5)) {
        strategyIds = [this.selectedId];
      } else {
        strategyIds = this.selectedIds;
      }
    }

    if (!strategyIds.filter(Boolean).length) return;

    this.isVisible = true;
    this.onStrategySelect(strategyIds.filter(Boolean) as number[]);
  }

  openVideoUrl(url: string): void {
    this.isVideoVisible = true;
    this.videoUrl = url;
  }

  onStrategySelect(id: number[]): void {
    const caseBehaviorId = this.caseParams?.behavior?.id;
    if (!caseBehaviorId) return;
    this.loaderService.show();
    this.handleStrategyCreation(id, caseBehaviorId);
  }

  openProactiveStrategyDrawer(data: StrategyItem): void {
    this.drawerService
      .openDrawer(
        ProactiveStrategyComponent,
        this.drawerBehaviorStrategies as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          data,
          mode: this.mode,
          behaviorId: this.caseParams?.behavior?.id,
          caseParams: this.caseParams,
        },
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isAppliedStep) {
          this.refreshAppliedStrategies();
        }
      });
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { type } = this.strategiesForm.value;
    if (this.isAppliedStep) {
      const currentSort = params.sort.find(item => item.value !== null);
      this.defaultParams.sortField = currentSort?.key || '';
      const queryParams = this.tableQueryService.buildQueryParams(
        params,
        this.defaultParams
      );

      this.loaderService.show();

      this.strategyService
        .getBehaviorStrategyList({
          caseBehaviorId: this.caseParams?.behavior?.id,
          strategyType: type ?? undefined,
          ...queryParams,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.loaderService.hide())
        )
        .subscribe({
          next: () => {
            this.defaultParams.pageIndex = params.pageIndex;
            this.defaultParams.pageSize = params.pageSize;
          },
          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
          },
        });
    } else {
      const queryParams = this.tableQueryService.buildQueryParams(
        params,
        this.defaultParams
      );

      this.loaderService.show();

      this.strategyService
        .getStrategyList({
          caseBehaviorId: this.caseParams?.behavior?.id,
          strategyType: type ?? undefined,
          ...queryParams,
        })
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => this.loaderService.hide())
        )
        .subscribe({
          next: () => {
            this.defaultParams.pageIndex = params.pageIndex;
            this.defaultParams.pageSize = params.pageSize;
          },
          error: ({ errors, message }: ErrorResponse) => {
            const errorMessages = errors.map(
              err => err.description || err.detailedMessage
            );
            this.commonService.notify('error', errorMessages, message);
          },
        });
    }
  }

  onPublishStrategyQueryParamsChange(params: NzTableQueryParams): void {
    if (this.selectedOption !== 'Create my own') return;

    const queryParams = this.tableQueryService.buildQueryParams(
      params,
      this.defaultParams
    );

    this.loaderService.show();

    this.strategyService
      .getPublishStrategyList({
        organizationId: this.caseParams.orgId ?? 0,
        caseBehaviorId: this.caseParams?.behavior?.id ?? 0,
        ...queryParams,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loaderService.hide())
      )
      .subscribe({
        next: () => {
          this.defaultParams.pageIndex = params.pageIndex;
          this.defaultParams.pageSize = params.pageSize;
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageStudentDrawerParams>(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.mode = params.mode;
          this.syncStepFromMode(this.mode);
          this.loadBipTemplate();
        }
        if (this.mode === 'edit') {
          this.setCurrentStep('selection');
        }
      });
  }

  private loadBipTemplate(): void {
    const bipTemplateId = this.caseParams.case?.bipTemplateId;
    if (!bipTemplateId) {
      this.bipTemplate = null;
      return;
    }
    this.bipTemplateGatingService
      .getTemplate(bipTemplateId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(template => {
        this.bipTemplate = template;
      });
  }

  private setCurrentStep(step: StrategyDrawerStep): void {
    this.currentStep = step;
    if (step === 'applied') {
      this.mode = 'view';
    } else if (step === 'selection') {
      this.mode = 'create';
    } else {
      this.mode = 'guide';
    }
  }

  private syncStepFromMode(mode: 'view' | 'edit' | 'create' | 'guide'): void {
    if (mode === 'view') {
      this.setCurrentStep('applied');
    }
  }

  private handleStrategyCreation(id: number[], caseBehaviorId: number): void {
    const { type } = this.strategiesForm.value;
    let allStrategies: StrategyItem[] = [];
    let selectedStrategies: StrategyItem[] = [];
    const strategiesSub = this.strategies$.subscribe(strats => {
      allStrategies = strats || [];
      selectedStrategies = allStrategies.filter(s => id.includes(s.id));
    });
    strategiesSub.unsubscribe();

    let newValue: Partial<Record<string, unknown>> = {};
    if (selectedStrategies.length > 0) {
      const { id: strategyId, title } = selectedStrategies[0];
      newValue = { id: strategyId, title };
    }
    const modifiedFields = this.auditContextService.buildFieldChanges(
      {},
      newValue,
      {
        excludeFields: ['id'],
        sensitiveFields: [],
        labelMap: { id: 'Strategy ID', title: 'Strategy Title' },
      }
    );

    this.strategyService
      .createStrategy({
        caseBehaviorId,
        behaviorStrategyIds: id,
        _modifiedFields: modifiedFields,
      })
      .pipe(
        tap(() => {
          this.commonService.notify(
            'success',
            'Behavior strategy added successfully'
          );
          if (this.selectedOption === 'Guide me') {
            this.strategyService
              .getStrategyList({
                caseBehaviorId: this.caseParams?.behavior?.id,
                strategyType: type ?? undefined,
                ...this.defaultParams,
              })
              .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                  this.loaderService.hide();
                  this.selectedId = null;
                  this.selectedIds = [];
                })
              )
              .subscribe({
                next: () => {
                  this.defaultParams.pageIndex = 1;
                  this.defaultParams.pageSize = 10;
                  this.selectedId = null;
                  this.selectedIds = [];
                },
                error: ({ errors, message }: ErrorResponse) => {
                  const errorMessages = errors.map(
                    err => err.description || err.detailedMessage
                  );
                  this.commonService.notify('error', errorMessages, message);
                },
              });
          } else {
            this.strategyService
              .getPublishStrategyList({
                organizationId: this.caseParams.orgId ?? 0,
                caseBehaviorId: this.caseParams?.behavior?.id ?? 0,
                ...this.defaultParams,
              })
              .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.loaderService.hide())
              )
              .subscribe({
                next: () => {
                  this.defaultParams.pageIndex = 1;
                  this.defaultParams.pageSize = 10;
                  this.selectedId = null;
                },
                error: ({ errors, message }: ErrorResponse) => {
                  const errorMessages = errors.map(
                    err => err.description || err.detailedMessage
                  );
                  this.commonService.notify('error', errorMessages, message);
                },
              });
          }
        }),
        catchError(({ errors, message }: ErrorResponse) => {
          this.commonService.notify(
            'error',
            errors?.map(err => err.description || err.detailedMessage) || [
              'An error occurred while creating the strategy.',
            ],
            message
          );
          this.selectedId = null;
          return EMPTY;
        }),
        finalize(() => {
          this.loaderService.hide();
          this.selectedId = null;
          this.selectedIds = [];
        })
      )
      .subscribe();
  }

  getStrategyTypeText(): string {
    const selectedType = this.strategiesForm.get('type')?.value;

    const strategyTexts: Record<number, string> = {
      71: 'Replacement Behavior strategies',
      72: 'Proactive strategies',
      73: 'Reactive strategies',
    };

    return selectedType ? strategyTexts[selectedType] : 'strategies';
  }

  getStatusText(status: number): string {
    const statusTexts: Record<number, string> = {
      1: 'Active',
      2: 'Inactive',
    };
    return statusTexts[status] || '';
  }

  openEditOwnStrategyComponent(data: StrategyItem): void {
    if (!this.allowManageStrategy) {
      return;
    }
    this.selectedId = data.id;
    this.drawerService
      .openDrawer(
        CreateOwnStrategyComponent,
        this.drawerBehaviorCreate as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          strategy: data,
          behaviorId: this.caseParams?.behavior?.id,
          mode: 'edit',
          caseParams: this.caseParams,
          strategyTypeId: this.strategiesForm.value.type ?? undefined,
        },
        DRAWER_IDS.CREATE_OWN_STRATEGY
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.mode = 'view';
        this.setCurrentStep('applied');
        this.refreshAppliedStrategies();
        this.resetState();
      });
  }

  onContinueToData(): void {
    if (this.caseParams.behavior) {
      const drawer = this.drawerService.getDrawerRef(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES
      );
      drawer?.close({ navigationHandled: true, isContinueProcessing: true });
      this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR);
      this.openDataDrawer(this.caseParams.behavior);
    }
  }
}
